import { useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, Tab, Tabs,
  TextField, Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Tune } from '@mui/icons-material';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import { withSerialNo } from '../../components/ui/gridColumns';
import api from '../../services/api';
import { getApiErrorMessage } from '../../services/userService';
import { formatMoney } from '../../utils/currency';
import { useTenantCurrency } from '../../hooks/useTenantCurrency';
import {
  ACCOUNT_LABELS, ACCOUNT_TYPES, AccountType, PersonView, dateText, fetchPaginated, personLabel, titleCase,
  useRewardsTenant,
} from './rewardsShared';

type LedgerTab = 'accounts' | 'transactions' | 'conversions';

type AccountRow = {
  _id: string;
  userId: string;
  user?: PersonView | null;
  accountType?: AccountType | null;
  points: number;
  tier?: string;
  lifetimePoints?: number;
  convertedPoints?: number;
  expiredPoints?: number;
  updatedAt?: string;
};

type TxRow = {
  _id: string;
  user?: PersonView | null;
  type: string;
  points: number;
  balanceAfter?: number;
  description?: string;
  service?: string;
  module?: string;
  referenceType?: string;
  amount?: number;
  currency?: string;
  expiresAt?: string;
  remaining?: number;
  createdBy?: PersonView | null;
  createdAt?: string;
};

const TX_TYPES = ['earn', 'convert', 'referral', 'adjust', 'expire', 'redeem', 'bonus'];

const ENDPOINTS: Record<LedgerTab, string> = {
  accounts: '/admin/rewards/points/accounts',
  transactions: '/admin/rewards/points/transactions',
  conversions: '/admin/rewards/points/conversions',
};

export default function PointsLedgerPage() {
  const tenant = useRewardsTenant();
  const { currency: tenantCurrency } = useTenantCurrency();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<LedgerTab>('accounts');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [accountType, setAccountType] = useState('all');
  const [type, setType] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [adjustFor, setAdjustFor] = useState<AccountRow | null>(null);

  const params = {
    page: page + 1,
    limit: pageSize,
    search: search.trim() || undefined,
    accountType: accountType === 'all' ? undefined : accountType,
    type: tab === 'transactions' && type !== 'all' ? type : undefined,
    from: tab !== 'accounts' ? from || undefined : undefined,
    to: tab !== 'accounts' ? to || undefined : undefined,
  };

  const { data, isFetching, error } = useQuery({
    queryKey: ['admin-points', tab, tenant.scopeKey, params],
    queryFn: () => fetchPaginated<AccountRow | TxRow>(ENDPOINTS[tab], params, tenant.headers),
    // Keep the previous page visible while paging/filtering, but never show another tab's
    // or tenant's rows under this tab's columns.
    placeholderData: (prev, prevQuery) => (
      prevQuery?.queryKey[1] === tab && prevQuery?.queryKey[2] === tenant.scopeKey ? keepPreviousData(prev) : undefined
    ),
    enabled: tenant.ready,
  });

  const accountColumns: GridColDef<AccountRow>[] = useMemo(() => [
    { field: 'user', headerName: 'Name', flex: 1, minWidth: 160, valueGetter: (_v, row) => personLabel(row.user) },
    { field: 'phone', headerName: 'Phone', width: 140, valueGetter: (_v, row) => row.user?.phone || '—' },
    { field: 'accountType', headerName: 'Account', width: 150, valueGetter: (_v, row) => (row.accountType ? ACCOUNT_LABELS[row.accountType] : '—') },
    { field: 'points', headerName: 'Balance', width: 110 },
    { field: 'tier', headerName: 'Tier', width: 110, renderCell: (p) => <Chip size="small" label={titleCase(p.row.tier)} /> },
    { field: 'lifetimePoints', headerName: 'Lifetime', width: 110 },
    { field: 'convertedPoints', headerName: 'Converted', width: 110 },
    { field: 'expiredPoints', headerName: 'Expired', width: 100 },
    {
      field: 'actions',
      headerName: '',
      width: 110,
      sortable: false,
      renderCell: (p) => (
        <Button size="small" startIcon={<Tune />} onClick={(e) => { e.stopPropagation(); setAdjustFor(p.row); }}>Adjust</Button>
      ),
    },
  ], []);

  const txColumns: GridColDef<TxRow>[] = useMemo(() => [
    { field: 'createdAt', headerName: 'Date', width: 165, valueFormatter: (v) => dateText(v as string) },
    { field: 'user', headerName: 'Name', flex: 1, minWidth: 150, valueGetter: (_v, row) => personLabel(row.user) },
    { field: 'accountType', headerName: 'Account', width: 140, valueGetter: (_v, row) => (row.user?.accountType ? ACCOUNT_LABELS[row.user.accountType] : '—') },
    { field: 'type', headerName: 'Type', width: 100, renderCell: (p) => <Chip size="small" label={titleCase(p.row.type)} /> },
    {
      field: 'points',
      headerName: 'Points',
      width: 100,
      renderCell: (p) => (
        <Typography variant="body2" fontWeight={600} color={p.row.points >= 0 ? 'success.main' : 'error.main'}>
          {p.row.points > 0 ? '+' : ''}{p.row.points}
        </Typography>
      ),
    },
    {
      field: 'amount',
      headerName: 'Value',
      width: 110,
      valueGetter: (_v, row) => (row.amount != null && row.amount > 0 ? formatMoney(row.amount, row.currency || tenantCurrency || undefined) : '—'),
    },
    { field: 'balanceAfter', headerName: 'Balance after', width: 120 },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
    { field: 'expiresAt', headerName: 'Expires', width: 165, valueFormatter: (v) => dateText(v as string) },
    { field: 'createdBy', headerName: 'By', width: 130, valueGetter: (_v, row) => (row.createdBy ? personLabel(row.createdBy) : '') },
  ], [tenantCurrency]);

  const reset = () => setPage(0);

  return (
    <PageShell>
      <PageHeader
        eyebrow="Referrals & Points"
        title="Points ledger"
        subtitle="Point balances, every earn / convert / expiry entry, and wallet conversions for all account types."
        breadcrumbs={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Points ledger' }]}
        actions={tenant.picker}
      />
      {tenant.notReady}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{getApiErrorMessage(error, 'Could not load points data')}</Alert>}

      <Tabs value={tab} onChange={(_, v) => { setTab(v); reset(); }} sx={{ mb: 2 }}>
        <Tab value="accounts" label="Accounts" />
        <Tab value="transactions" label="Transactions" />
        <Tab value="conversions" label="Wallet conversions" />
      </Tabs>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(5, 1fr)' }, gap: 1.5, mb: 2 }}>
        <TextField size="small" label="Search" placeholder="Name, phone or email" value={search} onChange={(e) => { setSearch(e.target.value); reset(); }} />
        <TextField select size="small" label="Account type" value={accountType} onChange={(e) => { setAccountType(e.target.value); reset(); }}>
          <MenuItem value="all">All</MenuItem>
          {ACCOUNT_TYPES.map((t) => <MenuItem key={t} value={t}>{ACCOUNT_LABELS[t]}</MenuItem>)}
        </TextField>
        {tab === 'transactions' && (
          <TextField select size="small" label="Type" value={type} onChange={(e) => { setType(e.target.value); reset(); }}>
            <MenuItem value="all">All types</MenuItem>
            {TX_TYPES.map((t) => <MenuItem key={t} value={t}>{titleCase(t)}</MenuItem>)}
          </TextField>
        )}
        {tab !== 'accounts' && (
          <>
            <TextField size="small" label="From" type="date" value={from} onChange={(e) => { setFrom(e.target.value); reset(); }} InputLabelProps={{ shrink: true }} />
            <TextField size="small" label="To" type="date" value={to} onChange={(e) => { setTo(e.target.value); reset(); }} InputLabelProps={{ shrink: true }} />
          </>
        )}
      </Box>

      <DataGrid
        key={tab}
        rows={data?.rows || []}
        columns={withSerialNo((tab === 'accounts' ? accountColumns : txColumns) as GridColDef[])}
        loading={isFetching}
        getRowId={(row) => row._id}
        autoHeight
        paginationMode="server"
        rowCount={data?.total ?? 0}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(m) => {
          if (m.pageSize !== pageSize) {
            setPageSize(m.pageSize);
            setPage(0);
          } else {
            setPage(m.page);
          }
        }}
        pageSizeOptions={[10, 20, 50, 100]}
        sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
      />

      {adjustFor && (
        <AdjustDialog
          account={adjustFor}
          headers={tenant.headers}
          onClose={() => setAdjustFor(null)}
          onDone={() => {
            setAdjustFor(null);
            queryClient.invalidateQueries({ queryKey: ['admin-points'] });
          }}
        />
      )}
    </PageShell>
  );
}

function AdjustDialog({
  account,
  headers,
  onClose,
  onDone,
}: {
  account: AccountRow;
  headers: Record<string, string>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [direction, setDirection] = useState<'add' | 'deduct'>('add');
  const [points, setPoints] = useState('');
  const [reason, setReason] = useState('');
  const amount = Math.floor(Number(points) || 0);

  const adjust = useMutation({
    mutationFn: () => api.post('/admin/rewards/points/adjust', {
      userId: account.userId || account.user?._id,
      points: direction === 'add' ? amount : -amount,
      reason: reason.trim(),
    }, { headers }),
    onSuccess: onDone,
  });

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Adjust points</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2">
            {personLabel(account.user)} — current balance <b>{account.points}</b> points
          </Typography>
          {adjust.error && <Alert severity="error">{getApiErrorMessage(adjust.error, 'Could not adjust points')}</Alert>}
          <TextField select size="small" label="Action" value={direction} onChange={(e) => setDirection(e.target.value as 'add' | 'deduct')}>
            <MenuItem value="add">Add points</MenuItem>
            <MenuItem value="deduct">Deduct points</MenuItem>
          </TextField>
          <TextField size="small" type="number" label="Points" value={points} onChange={(e) => setPoints(e.target.value)} inputProps={{ min: 1 }} />
          <TextField size="small" multiline minRows={2} label="Reason (shown in history)" value={reason} onChange={(e) => setReason(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={amount <= 0 || !reason.trim() || adjust.isPending} onClick={() => adjust.mutate()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
